import { Link } from "react-router-dom";
import { ArrowRight, Compass, Home, Search } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import usePageMeta from "@/hooks/usePageMeta";

export default function NotFoundPage() {
  usePageMeta({
    title: "Page not found · Luminary Insights & Advisory",
    description: "We couldn't find that page. Explore our Industries, Trackers, Frameworks, or schedule a Strategy Session.",
    path: "/404",
  });
  return (
    <PublicLayout>
      <section className="border-b border-[var(--border)] bg-white py-24 sm:py-32 grain" data-testid="not-found-page">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--gold)]">Error 404</div>
              <h1 className="font-display font-black text-[clamp(2.4rem,6vw,5rem)] mt-3 leading-[0.95] tracking-tight text-[var(--navy)]">
                That page<br/>
                <span className="italic font-medium text-[var(--gold)]">is off-record.</span>
              </h1>
              <p className="mt-8 font-display italic text-2xl text-[var(--slate)] max-w-xl leading-snug">
                The link you followed doesn&apos;t lead anywhere we publish. Let&apos;s redirect you to something more useful.
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/" className="btn-primary" data-testid="notfound-home">
                  <Home size={13} /> Back to home
                </Link>
                <Link to="/trackers" className="btn-secondary" data-testid="notfound-trackers">
                  Industry Trackers <ArrowRight size={13} />
                </Link>
                <Link to="/strategy-session" className="btn-ghost" data-testid="notfound-session">
                  Schedule Strategy Session
                </Link>
              </div>
            </div>
            <aside className="lg:col-span-5">
              <div className="bg-[var(--navy)] text-white p-6 sm:p-7">
                <Compass size={20} className="text-[var(--gold)]" />
                <div className="eyebrow text-[var(--gold)] mt-4">Try these instead</div>
                <ul className="mt-5 space-y-3 text-[14px]">
                  {[
                    ["/industries", "12 Industries we cover"],
                    ["/modules", "15 Intelligence Modules"],
                    ["/frameworks", "7 Proprietary Frameworks"],
                    ["/luminous", "Luminous AI brief generator"],
                    ["/firm", "About the firm"],
                  ].map(([to, label]) => (
                    <li key={to} className="border-b border-white/10 pb-2.5 last:border-0">
                      <Link to={to} className="flex items-center justify-between gap-4 hover:text-[var(--gold)] transition-colors" data-testid={`notfound-link-${to.replace(/\W+/g, "-")}`}>
                        <span>{label}</span>
                        <ArrowRight size={12} className="opacity-60" />
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link to="/search" className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--gold)] hover:text-white transition-colors" data-testid="notfound-search">
                  <Search size={12} /> Or search the site
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
